'use client'

/* eslint-disable no-console */
import { useEffect } from 'react'
import { sendEnterpriseEvent } from '@utils/enterpriseSegmentEvent'
import { TAP_EVENT, EVENT_TYPE } from '@constants/enterpriseSegmentData'

const TARGET_FORM_ID = 'd5018fa1-ed08-4739-9390-20d27da5d840'

declare global {
  interface Window {
    HubSpotFormsV4: {
      getForms: () => Array<{
        getFormFieldValues: () => Promise<Record<string, any>>
      }>
      getFormFromEvent: (event: any) => {
        getFormFieldValues: () => Promise<Record<string, any>>
      }
    }
  }
}

// Map HubSpot field names to required key names
const FIELD_NAME_MAP: Record<string, string> = {
  firstname: 'first_name',
  company: 'company_name',
  eb_client_industry: 'industry',
  numemployees: 'num_of_employees',
  phone: 'phone',
  email: 'email',
  message: 'message',
  interested_in: 'interested_in',
}

// Transform HubSpot form values to flat key-value pairs with renamed keys
const transformFormValues = (values: Record<string, any>): Record<string, any> => {
  const transformedData: Record<string, any> = {}

  Object.keys(values).forEach(key => {
    const field = values[key]
    if (field && typeof field === 'object' && 'name' in field && 'value' in field) {
      // Extract field name (remove "0-1/" prefix)
      const fieldName = field.name.split('/').pop() || field.name
      // Map to the required key name
      const mappedFieldName = FIELD_NAME_MAP[fieldName] || fieldName
      transformedData[mappedFieldName] = field.value
    }
  })

  return transformedData
}

const EnterpriseFormTracker = () => {
  useEffect(() => {
    const handleFormSubmission = async (event: any) => {
      const formId = event?.detail?.formId

      if (formId !== TARGET_FORM_ID) {
        return
      }

      // Check if HubSpot library is loaded
      if (!window.HubSpotFormsV4?.getFormFromEvent) {
        return
      }

      try {
        // Use HubSpot API to get form field values
        const form = window.HubSpotFormsV4.getFormFromEvent(event)
        const values = await form.getFormFieldValues()

        // Transform form values
        const transformedData = transformFormValues(values)

        // Get additional page properties
        const pathname = window.location.pathname
        const search = window.location.search
        const title = document.title
        const referrer = document.referrer || ''

        const payload = {
          ...transformedData,
          page_type: '',
          path: pathname,
          source: referrer,
          referrer,
          title,
          search,
          form_id: formId,
          action_name: 'lead_form_submitted',
          action_type: TAP_EVENT.SUBMIT_LEAD_FORM,
        }

        void sendEnterpriseEvent(payload, EVENT_TYPE.ACTION)
      } catch (error) {}
    }

    const wrappedHandler = (event: any) => {
      void handleFormSubmission(event)
    }

    window.addEventListener('hs-form-event:on-submission:success', wrappedHandler)

    return () => {
      window.removeEventListener('hs-form-event:on-submission:success', wrappedHandler)
    }
  }, [])

  return null
}

export default EnterpriseFormTracker
